import React, { useState, useCallback } from 'react';
import { HiOutlineEye, HiOutlineEyeOff, HiMenuAlt4, HiX, HiRefresh } from 'react-icons/hi';

// Section configuration types
export interface SectionConfig {
    id: string;
    label: string;
    description: string;
    visible: boolean;
    order: number;
    // Some sections are conditional (only show when certain data exists)
    isConditional?: boolean;
}

export interface SectionPreferences {
    sections: SectionConfig[];
    version: number; // For migration if we add new sections later
}

// Default section configuration
export const DEFAULT_SECTIONS: SectionConfig[] = [
    {
        id: 'effort-metrics',
        label: 'Effort Metrics',
        description: 'Story Points & Original Estimate cards',
        visible: true,
        order: 0,
    },
    {
        id: 'status-overview',
        label: 'Status Overview',
        description: 'Donut chart with status breakdown',
        visible: true,
        order: 1,
    },
    {
        id: 'workload-by-person',
        label: 'Workload by Person',
        description: 'Task distribution per team member',
        visible: true,
        order: 2,
    },
    {
        id: 'top-work-units',
        label: 'Top Work Units',
        description: 'Work unit/project breakdown',
        visible: true,
        order: 3,
    },
    {
        id: 'work-item-types',
        label: 'Work Item Types',
        description: 'Task type distribution chart',
        visible: true,
        order: 4,
    },
    // Sprint-related sections (visible when multiple sprints selected)
    {
        id: 'sprint-comparison',
        label: 'Sprint Comparison',
        description: 'Compare metrics across sprints (includes Work Item Types by Sprint)',
        visible: true,
        order: 5,
        isConditional: true,
    },
    // Team-related sections (visible when multiple teams selected)
    {
        id: 'team-comparison',
        label: 'Team Comparison',
        description: 'Compare metrics across teams (includes Work Item Types by Team)',
        visible: true,
        order: 6,
        isConditional: true,
    },
];

const STORAGE_KEY = 'taskAnalyticsSectionPreferences';
const CURRENT_VERSION = 3; // Bumped to remove sub-sections that can't be reordered

// Load preferences from localStorage
export const loadSectionPreferences = (): SectionConfig[] => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed: SectionPreferences = JSON.parse(stored);

            // Handle version migration if needed
            if (parsed.version === CURRENT_VERSION) {
                // Merge with defaults to handle any new sections added
                const storedIds = new Set(parsed.sections.map(s => s.id));
                const mergedSections = [...parsed.sections];

                // Add any new sections from defaults that don't exist in stored
                DEFAULT_SECTIONS.forEach(defaultSection => {
                    if (!storedIds.has(defaultSection.id)) {
                        mergedSections.push({
                            ...defaultSection,
                            order: mergedSections.length,
                        });
                    }
                });

                return mergedSections.sort((a, b) => a.order - b.order);
            }
        }
    } catch (error) {
        console.error('Failed to load section preferences:', error);
    }
    return [...DEFAULT_SECTIONS];
};

// Save preferences to localStorage
export const saveSectionPreferences = (sections: SectionConfig[]): void => {
    try {
        const prefs: SectionPreferences = {
            sections,
            version: CURRENT_VERSION,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (error) {
        console.error('Failed to save section preferences:', error);
    }
};

interface SectionCustomizerProps {
    isOpen: boolean;
    onClose: () => void;
    sections: SectionConfig[];
    onSectionsChange: (sections: SectionConfig[]) => void;
    // Conditional section availability
    showSprintComparison?: boolean;
    showTeamComparison?: boolean;
}

const SectionCustomizer: React.FC<SectionCustomizerProps> = ({
    isOpen,
    onClose,
    sections,
    onSectionsChange,
    showSprintComparison = false,
    showTeamComparison = false,
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    // Filter sections based on conditional availability
    const visibleSections = sections.filter(section => {
        // Always show non-conditional sections
        if (!section.isConditional) return true;

        // Sprint-related sections (visible when multiple sprints selected)
        if (section.id === 'sprint-comparison') {
            return showSprintComparison;
        }

        // Team-related sections (visible when multiple teams selected)
        if (section.id === 'team-comparison') {
            return showTeamComparison;
        }

        return true;
    });

    // Handle drag start
    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        // Add a slight delay to show the dragging visual
        const target = e.target as HTMLElement;
        setTimeout(() => {
            target.style.opacity = '0.5';
        }, 0);
    }, []);

    // Handle drag end
    const handleDragEnd = useCallback((e: React.DragEvent) => {
        setDraggedIndex(null);
        setDragOverIndex(null);
        const target = e.target as HTMLElement;
        target.style.opacity = '1';
    }, []);

    // Handle drag over
    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    }, []);

    // Handle drop
    const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        const dragIndex = draggedIndex;

        if (dragIndex === null || dragIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newSections = [...sections];
        const [draggedItem] = newSections.splice(dragIndex, 1);
        newSections.splice(dropIndex, 0, draggedItem);

        // Update order values
        const updatedSections = newSections.map((section, idx) => ({
            ...section,
            order: idx,
        }));

        onSectionsChange(updatedSections);
        saveSectionPreferences(updatedSections);

        setDraggedIndex(null);
        setDragOverIndex(null);
    }, [draggedIndex, sections, onSectionsChange]);

    // Toggle section visibility
    const toggleVisibility = useCallback((sectionId: string) => {
        const updatedSections = sections.map(section =>
            section.id === sectionId
                ? { ...section, visible: !section.visible }
                : section
        );
        onSectionsChange(updatedSections);
        saveSectionPreferences(updatedSections);
    }, [sections, onSectionsChange]);

    // Reset to defaults
    const handleReset = useCallback(() => {
        const defaultSections = [...DEFAULT_SECTIONS];
        onSectionsChange(defaultSections);
        saveSectionPreferences(defaultSections);
    }, [onSectionsChange]);

    // Move section up
    const moveUp = useCallback((index: number) => {
        if (index === 0) return;
        const newSections = [...sections];
        [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
        const updatedSections = newSections.map((section, idx) => ({
            ...section,
            order: idx,
        }));
        onSectionsChange(updatedSections);
        saveSectionPreferences(updatedSections);
    }, [sections, onSectionsChange]);

    // Move section down
    const moveDown = useCallback((index: number) => {
        if (index === sections.length - 1) return;
        const newSections = [...sections];
        [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
        const updatedSections = newSections.map((section, idx) => ({
            ...section,
            order: idx,
        }));
        onSectionsChange(updatedSections);
        saveSectionPreferences(updatedSections);
    }, [sections, onSectionsChange]);

    if (!isOpen) return null;

    return (
        <div className="absolute inset-0 z-50 bg-white dark:bg-slate-800 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-800 dark:to-slate-700">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                        <HiMenuAlt4 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-white">Customize Sections</h3>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">Drag to reorder • Toggle visibility</p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-500 dark:text-gray-400 transition-colors"
                >
                    <HiX className="w-5 h-5" />
                </button>
            </div>

            {/* Section List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                {visibleSections.map((section) => {
                    // Find the actual index in the original sections array
                    const index = sections.findIndex(s => s.id === section.id);

                    return (
                        <div
                            key={section.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            className={`
              group flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 cursor-grab active:cursor-grabbing
              ${dragOverIndex === index
                                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500 shadow-lg scale-[1.02]'
                                    : section.visible
                                        ? 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700/50 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-md'
                                        : 'border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 opacity-60 hover:opacity-80'
                                }
            `}
                        >
                            {/* Drag Handle */}
                            <div className="flex flex-col gap-0.5 text-gray-400 dark:text-gray-500 group-hover:text-blue-500 transition-colors">
                                <div className="flex gap-0.5">
                                    <div className="w-1 h-1 rounded-full bg-current" />
                                    <div className="w-1 h-1 rounded-full bg-current" />
                                </div>
                                <div className="flex gap-0.5">
                                    <div className="w-1 h-1 rounded-full bg-current" />
                                    <div className="w-1 h-1 rounded-full bg-current" />
                                </div>
                                <div className="flex gap-0.5">
                                    <div className="w-1 h-1 rounded-full bg-current" />
                                    <div className="w-1 h-1 rounded-full bg-current" />
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className={`text-sm font-semibold ${section.visible ? 'text-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                                        {section.label}
                                    </span>
                                </div>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{section.description}</p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1">
                                {/* Move up/down buttons (mobile-friendly alternative to drag) */}
                                <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); moveUp(index); }}
                                        disabled={index === 0}
                                        className={`p-0.5 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${index === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                        title="Move up"
                                    >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); moveDown(index); }}
                                        disabled={index === sections.length - 1}
                                        className={`p-0.5 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${index === sections.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                        title="Move down"
                                    >
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Visibility toggle */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); toggleVisibility(section.id); }}
                                    className={`p-2 rounded-lg transition-all duration-200 ${section.visible
                                        ? 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60'
                                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-500 dark:hover:bg-slate-600'
                                        }`}
                                    title={section.visible ? 'Hide section' : 'Show section'}
                                >
                                    {section.visible ? (
                                        <HiOutlineEye className="w-4 h-4" />
                                    ) : (
                                        <HiOutlineEyeOff className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/50">
                <button
                    onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                >
                    <HiRefresh className="w-4 h-4" />
                    Reset to Defaults
                </button>
                <button
                    onClick={onClose}
                    className="px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                    Done
                </button>
            </div>
        </div>
    );
};

export default SectionCustomizer;
